import { CashTransaction } from '../interfaces/cash-reconciliation.interface';
import {
  STATUS_KEYS,
  StatusKey,
  MATCH_STATUS,
  STATUS_TRIGGER,
  DEFAULT_STATUS_TRIGGER,
  STATUS_OPTION_LIST,
  MatchStatus,
  OpeningStatus
} from '../constants/cash-reconciliation.constants';

export function mapBadgeToKind(
  badge: string | null
): CashTransaction['kind'] {
  switch (badge) {
    case 'opening_balance':
      return 'opening';
    case 'closing_balance':
      return 'closing';
    case 'extra_added_cash':
      return 'extra';
    default:
      return 'order';
  }
}

export function normalizeStatus(status: string): string {
  if (!status) return 'other';

  return status
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_'); // "Day off" → "day_off"
}

/** Expected = opening + extra + payment */
export function calculateExpected(
  opening: number | null,
  extraCash: number | null,
  payment: number | null
): number {
  return (
    (Number(opening) || 0) +
    (Number(extraCash) || 0) +
    (Number(payment) || 0)
  );
}

export function calculateDelta(
  closing: number | null,
  expected: number
): number | null {

  if (closing == null) {
    return null;
  }

  return Number(closing) - expected;
}

/** Single source of truth for status */
export function getFixStatusKey(
  opening: number | null,
  closing: number | null,
  openingStatus: OpeningStatus,
  delta: number | null
): StatusKey {
  const isClosingEmpty = closing == null || closing === 0;
  const isBothEmpty =
    (opening == null || opening === 0) &&
    isClosingEmpty;

  if (openingStatus === 'send') {
    return STATUS_KEYS.PENDING;
  }

  if (openingStatus === 'sent' && isClosingEmpty) {
    return STATUS_KEYS.ENTRY_NOT_DONE;
  }

  if (isBothEmpty) {
    return STATUS_KEYS.PENDING;
  }

  if (isClosingEmpty) {
    return STATUS_KEYS.ENTRY_NOT_DONE;
  }

  return delta === 0
    ? STATUS_KEYS.MATCHED
    : STATUS_KEYS.UNMATCHED;
}

export function recomputeEntry<
  T extends {
    opening_balance: number | null;
    closing_balance: number | null;
    extra_cash?: number | null;
    payment_received?: number | null;
    opening_status?: OpeningStatus | null;
    extra_cash_total?: number | null;
  }
>(entry: T) {

  const opening = Number(entry.opening_balance ?? 0);
  const extra = Number(entry.extra_cash_total ?? 0);
  const payment = Number(entry.payment_received ?? 0);

  const expected = calculateExpected(opening, extra, payment);

  let delta = calculateDelta(
    entry.closing_balance,
    expected
  );

  const statusKey = getFixStatusKey(
    entry.opening_balance,
    entry.closing_balance,
    entry.opening_status ?? 'send',
    delta
  );
  // force delta = 0 for non-reconciliation states
  if (
    statusKey === STATUS_KEYS.PENDING ||
    statusKey === STATUS_KEYS.ENTRY_NOT_DONE
  ) {
    delta = 0;
  }

  const matchStatus =
    statusKey === STATUS_KEYS.MATCHED
      ? 'matched'
      : statusKey === STATUS_KEYS.UNMATCHED
        ? 'unmatched'
        : '--';

  return {
    ...entry,
    expected_closing: expected,
    delta,
    match_status: matchStatus,
    status_key: statusKey,
    status: MATCH_STATUS[statusKey]?.label ?? '—'
  };
}

export function getShiftLabel(shift: any): string {
  if (!shift) return '--';
  // case 1: object
  if (typeof shift === 'object') {
    return shift.name || '--';
  }

  // case 2: string
  if (typeof shift === 'string') {
    return shift;
  }

  return '';
}

export function getStatusLabel(status: string): string {
  const option = STATUS_OPTION_LIST.find((s) => s.value === status);
  return option?.label || status || '--';
}

export function getStatusStyle(status: string): Record<string, string> {
  const style = STATUS_TRIGGER[status] || DEFAULT_STATUS_TRIGGER;

  return {
    background: style.bg,
    color: style.color
  };
}

export function normalizeDeltaStatus(
  status?: string | null
): StatusKey {
  const normalized = (status || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_'); // spaces or hyphens → underscore

  switch (normalized) {
    case 'matched':
    case 'match':
      return STATUS_KEYS.MATCHED;

    case 'unmatched':
    case 'not_matched':
      return STATUS_KEYS.UNMATCHED;

    case 'entry_not_done':
      return STATUS_KEYS.ENTRY_NOT_DONE;

    case 'pending':
    default:
      return STATUS_KEYS.PENDING;
  }
}

export function shouldShowDelta(
  status?: string | null
): boolean {
  return normalizeDeltaStatus(status) === STATUS_KEYS.UNMATCHED;
}

export function getOpeningStatusLabel(
  status: string | null | undefined
): string {
  switch ((status || '').toLowerCase()) {
    case 'send':
      return 'Send';
    case 'sent':
      return 'Sent';
    default:
      return 'Send';
  }
}

export function getOpeningButtonLabel(
  currentOpening: number,
  draftOpening: number,
  status: string | null | undefined
): string {
  const hasChanged = Number(currentOpening) !== Number(draftOpening);
  if (hasChanged) {
    return getOpeningStatusLabel('send');
  }
  return getOpeningStatusLabel(status);
}

/**
 * Start Reusable delta status resolver
 */
export function getDeltaMatchStatus(
  openingStatus: OpeningStatus | string | null | undefined,
  closingBalance: number | null | undefined,
  delta: number | null | undefined
): MatchStatus | '--' {
  const normalizedOpening = (openingStatus || '').toLowerCase();
  const closing = Number(closingBalance ?? 0);
  const deltaValue = Number(delta ?? 0);
  /**
   * send + 0 closing
   * => --
   */
  if (
    normalizedOpening === 'send' &&
    closing === 0
  ) {
    return '--';
  }

  /**
   * sent + 0 closing
   * => entry_not_done
   */
  if (
    normalizedOpening === 'sent' &&
    closing === 0
  ) {
    return STATUS_KEYS.ENTRY_NOT_DONE;
  }

  /**
   * sent + closing > 0
   */
  if (
    normalizedOpening === 'sent' &&
    closing > 0
  ) {
    if (deltaValue === 0) {
      return STATUS_KEYS.MATCHED;
    }
    return STATUS_KEYS.UNMATCHED;
  }
  return '--';
}

/**
 * Whether delta should render as plain text
 */
export function isPlainDeltaStatus(
  status: MatchStatus | '--'
): boolean {
  return status === '--';
}

/**
 * Reusable label resolver
 */
export function getDeltaStatusLabel(
  status: MatchStatus | '--'
): string {
  if (status === '--') {
    return '--';
  }
  return MATCH_STATUS[status]?.label || '--';
}

/**
 * Reusable color resolver
 */
export function getDeltaStatusStyle(
  status: MatchStatus | '--'
): { background: string; color: string } {

  if (status === '--') {
    return {
      background: 'transparent',
      color: 'var(--gray-500)'
    };
  }

  return {
    background: MATCH_STATUS[status]?.bg || 'transparent',
    color: MATCH_STATUS[status]?.text || 'inherit'
  };
}

/**
 * End reusable delta status resolver
 */